import React, { useContext, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import eventEmitter from "../shared/events";

//Layouts
import AssignmentLayout from "./assignment/layout";
import AttributesLayout from "./attributes/layout";
import ClientsLayout from "./clients/layout";
import UserRolesLayout from "./user-roles/layout";
import SettingsLayout from "./settings/layout";

//Indexes
import AdminIndex from "./index";
import AssignmentIndex from "./assignment";
import LinkingManagerIndex from "./linking-manager";
import MonitoringIndex from "./monitoring";
import NotificationsIndex from "./settings/notifications";
import PermissionsIndex from "./user-roles/permissions";
import RenewIndex from "./renew";
import ResubmitUpcIndex from "./resubmit-upc";
import RolesIndex from "./user-roles/roles";
import RolePermisscionsIndex from "./user-roles/role-permissions";
import UpcSearchIndex from "./upc-search";
import UserDetailIndex from "./user-roles/user-detail";
import UsersIndex from "./user-roles/users";
import { WorkflowIndex } from "./assignment/workflow";

//Side Navigation Links
import AttributeSettings from "./attributes/attribute-settings";
import ClientSettings from "./clients/client-settings";
import SLAGroups from "./user-roles/sla-groups";
import PicklistData from "./attributes/picklist-data";

//Contexts
import { SettingsContext } from "contexts/settings";

//Hooks
import { useAlert } from "hooks/useAlert";

const EVENT_NAVIGATION = "NAVIGATION_EVENT";
const withAssignmentLayout = (Component) => (props) => (
    <AssignmentLayout location={props.location}>
        <Component {...props} />
    </AssignmentLayout>
);
const withAttributesLayout = (Component) => (props) => (
    <AttributesLayout location={props.location}>
        <Component {...props} />
    </AttributesLayout>
);
const withClientsLayout = (ComponentComp) => (props) => (
    <ClientsLayout location={props.location}>
        <ComponentComp {...props} />
    </ClientsLayout>
);
const withUserRolesLayout = (ComponentComp) => (props) => (
    <UserRolesLayout location={props.location}>
        <ComponentComp {...props} />
    </UserRolesLayout>
);
const withSettingsLayout = (Component) => (props) => (
    <SettingsLayout>
        <Component {...props} />
    </SettingsLayout>
);

const TaskAssignment = withAssignmentLayout(AssignmentIndex);
const WorkflowAssignment = withAssignmentLayout(WorkflowIndex);
const Users = withUserRolesLayout(UsersIndex);
const UserDetails = withUserRolesLayout(UserDetailIndex);
const Roles = withUserRolesLayout(RolesIndex);
const RolePermissions = withUserRolesLayout(RolePermisscionsIndex);
const Permissions = withUserRolesLayout(PermissionsIndex);
const Settings = withClientsLayout(ClientSettings);
const SLAsGroups = withClientsLayout(SLAGroups);
const Picklist = withAttributesLayout(PicklistData);
const AttributesOptions = withAttributesLayout(AttributeSettings);
const Notifications = withSettingsLayout(NotificationsIndex);

const AdminRoutes = () => {
    const { toggleAlert } = useAlert();
    const push = useNavigate();
    const { settings } = useContext(SettingsContext);

    const navigate = (path) => {
        const pathname = window.location.pathname;
        const currentUrl = pathname.endsWith("/") ? pathname.substring(0, pathname.length - 1) : pathname;

        if (!path || path.length === 0) {
            const regex = new RegExp("/[^/]*$");
            push(currentUrl.replace(regex, "/"));

            return;
        }

        push(`${currentUrl}/${path}`);
    };

    useEffect(() => {
        if (settings.globalNotifications.messages.length) {
            toggleAlert();
        }
    }, [settings.globalNotifications.messages.length]);

    useEffect(() => {
        eventEmitter.on(EVENT_NAVIGATION, navigate);

        return () => eventEmitter.removeListener(EVENT_NAVIGATION, navigate);
    }, []);

    return (
        <Routes>
            <Route
                exact
                path="/"
                element={<AdminIndex />}
            />
            <Route
                path="assignment/tasks"
                element={<TaskAssignment />}
            />
            <Route
                path="assignment/workflows"
                element={<WorkflowAssignment />}
            />
            <Route
                path="enew"
                element={<RenewIndex />}
            />
            <Route
                path="resubmit-upc"
                element={<ResubmitUpcIndex />}
            />
            <Route
                path="monitoring"
                element={<MonitoringIndex />}
            />
            <Route
                path="upc-search"
                element={<UpcSearchIndex />}
            />
            <Route
                path="linking-manager/:id"
                element={<LinkingManagerIndex />}
            />
            <Route
                path="users"
                element={<Users />}
            />
            <Route
                path="user/:id"
                element={<UserDetails />}
            />
            <Route
                path="roles"
                element={<Roles />}
            />
            <Route
                path="role/:id"
                element={<RolePermissions />}
            />
            <Route
                path="permissions"
                element={<Permissions />}
            />
            <Route
                path="clients/settings"
                element={<Settings />}
            />
            <Route
                path="clients/groups"
                element={<SLAsGroups />}
            />
            <Route
                path="attributes/picklist-data"
                element={<Picklist />}
            />
            <Route
                path="attributes/attribute-settings"
                element={<AttributesOptions />}
            />
            <Route
                path="settings/notifications"
                element={<Notifications />}
            />
            <Route
                path="*"
                element={
                    <Navigate
                        to="/admin"
                        replace
                    />
                }
            />
        </Routes>
    );
};

export default AdminRoutes;
